import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Divider, Panel, Button } from '@gasbuddy/react-components';
import { faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './styles.css';
import sharedStyles from '../../../styles/shared.css';
import { StationReviewType } from '../../../utils/prop-types/review-types';
import ReviewRatings from '../review-ratings';
import EmailReviewButton from '../email-review-button';
import FlagReviewButton from '../flag-review-button';
import StarReviewButton from '../star-review-button';
import ShareReviewButton from '../share-review-button';
import ReplyReview from '../reply-review';
import Animate from '../../animate';
import ReviewReplyList from '../review-reply-list';
import BrandPropType from '../../../utils/prop-types/brand';
import ErrorBoundary from '../../error-boundary/error-boundary';

const { canReplyReview } = require('../../../../lib/permissions').default;

const canReply = canReplyReview();

export default function ReviewCard({
  review: reviewProps,
  insideModal,
  brandsMap,
  onClick,
  className,
  shareable,
  ...rest
}) {
  const [showReplyCard, setShowReplyCard] = useState(false);
  const [review, setReview] = useState(reviewProps);
  const isRatingOnlyReview = !reviewProps.text?.length;

  const handleOnClickReplyButton = useCallback(() => {
    setShowReplyCard(!showReplyCard);
  }, [showReplyCard]);

  const handleOnReplyCreated = useCallback(
    (newReply) => {
      const { replies } = review;
      setReview({
        ...review,
        replies: [newReply, ...replies],
      });
      setShowReplyCard(false);
    },
    [review]
  );

  const handleOnClick = useCallback(() => {
    onClick(review.id);
  }, [onClick, review]);

  return (
    <Panel
      data-review-id={review.id}
      borderedcolor="white"
      className={`${styles.card} ${className}`}
      onClick={handleOnClick}
      {...rest}
    >
      <div>
        <div className={styles.avatarWrapper}>
          <img
            src={
              review.member.avatar_url
              || 'https://images.gasbuddy.com/images/websites/gasbuddy/default-avatar.jpg'
            }
            alt=""
            className={styles.avatar}
          />
        </div>
        <div className={sharedStyles.pullLeft}>
          <div className={styles.username}>{review.member.username}</div>
          <div>
            {`${moment(review.create_date).format(
              'MMM DD, YYYY [at] hh:mma'
            )} - Review ID # ${review.id}`}
          </div>
          <div>
            <a
              href={`/client/station-profiles/station-profile?stationId=${review.station.id}`}
              className={styles.stationUrl}
            >
              {`${review.station.retailer_name} - #${review.station.retailer_reference}`}
            </a>
            {` ${review.station.address.address} ${review.station.address.city}, ${review.station.address.state}`}
          </div>
        </div>
        {!insideModal && (
          <div className={`${sharedStyles.pullRight} ${styles.buttonPanel}`}>
            {!review.is_visible && (
              <FontAwesomeIcon
                icon={faEyeSlash}
                size="2x"
                style={{ marginLeft: '32px', marginTop: '8px' }}
                title="This review is hidden"
              />
            )}
            {shareable && (
              <div>
                <ShareReviewButton />
              </div>
            )}
            {review.text && review.is_reportable && (
              <div>
                <FlagReviewButton reviewId={review.id} isReportable />
              </div>
            )}
            <div>
              <StarReviewButton reviewId={review.id} starred={review.starred} />
            </div>
            <div className={canReply ? '' : sharedStyles.pullRight}>
              <EmailReviewButton review={review} />
            </div>
            {canReply && !isRatingOnlyReview && (
              <div className={sharedStyles.pullRight}>
                <Button
                  type="button"
                  className={`${sharedStyles.linkButton} ${styles.replyButton}`}
                  link
                  onClick={handleOnClickReplyButton}
                >
                  REPLY
                </Button>
              </div>
            )}
          </div>
        )}
        <div className="clearfix" />
      </div>

      <Divider />
      <div>
        {review.text || (
          <span className={styles.nonWrittenReview}>
            Customer rated this station without leaving a review
          </span>
        )}
      </div>

      <Divider className={styles.extendingDivider} />
      <ErrorBoundary>
        <ReviewRatings
          overallRating={review.sentiment}
          categoryRatings={review.ratings}
          insideModal={insideModal}
        />
      </ErrorBoundary>

      <ErrorBoundary>
        <Animate className={styles.extendingDivider} show={showReplyCard}>
          <Divider className={styles.extendingDivider} />
          <ReplyReview review={review} onReplyCreated={handleOnReplyCreated} />
        </Animate>
      </ErrorBoundary>
      {!insideModal && (
        <ErrorBoundary>
          <ReviewReplyList
            reviewId={review.id}
            replies={review.replies}
            brandsMap={brandsMap}
          />
        </ErrorBoundary>
      )}
    </Panel>
  );
}

ReviewCard.propTypes = {
  review: StationReviewType.isRequired,
  insideModal: PropTypes.bool,
  brandsMap: PropTypes.shape({
    brandId: BrandPropType,
  }),
  onClick: PropTypes.func,
  className: PropTypes.string,
  shareable: PropTypes.bool,
};

ReviewCard.defaultProps = {
  insideModal: false,
  brandsMap: {},
  onClick: () => { },
  className: null,
  shareable: false,
};
